import { QueryCondition } from '../types';

export function generateCqlFilterString(condition: QueryCondition): string {
  let cql_string = '';

  // Handle multiple values: expand complex attributes, IN() for simple attributes
  if (Array.isArray(condition.value) && condition.value.length > 1) {
    const complexAttributes = new Set([
      'phsym',
      'text1',
      'text2',
      'irrig',
      'stone',
      'salsym',
      'slope_rg',
      'slpl',
      'pasteros',
    ]);

    if (complexAttributes.has(condition.attribute)) {
      const expandedConditions = condition.value
        .map((val) => {
          const singleCondition: QueryCondition = {
            ...condition,
            value: val,
          };
          const part = generateCqlFilterString(singleCondition);
          return `(${part})`;
        })
        .join(' OR ');

      return expandedConditions.length > 0 ? `(${expandedConditions})` : '';
    }

    // Simple attributes: direct IN clause
    cql_string = condition.attribute;
    cql_string += " in ('";
    cql_string += condition.value.join("', '");
    cql_string += "')";
    return cql_string;
  }

  // For single value or complex attributes, use the original logic
  const singleValue = Array.isArray(condition.value) ? condition.value[0] : condition.value;

  if (condition.attribute === 'slope_rg' || condition.attribute === 'slpl') {
    cql_string = condition.attribute;
    cql_string += ` like '${singleValue.charAt(0)}%'`;
  } else if (condition.attribute === 'phsym') { 
    cql_string = condition.attribute;
    switch (singleValue) {
      case "1":
        cql_string += " in ('X2', 'X4', 'X6', 'X0', 'X1', 'X3', 'X5')";
        break;
      case "2":
        cql_string += " in ('A2', 'A4', 'A6', 'A0', 'A1', 'A3', 'A5')";
        break;
      case "3":
        cql_string += " in ('B2', 'B4', 'B6', 'B0', 'B1', 'B3', 'B5')";
        break;
      case "4":
        cql_string += " in ('C2', 'C4', 'C6', 'C0', 'C1', 'C3', 'C5')";
        break;
      case "5":
        cql_string += " in ('D2', 'D4', 'D6', 'D0', 'D1', 'D3', 'D5')";
        break;
    }
  } else if (condition.attribute === 'pasteros') {
    cql_string = condition.attribute;
    switch (singleValue) {
      case '1':
        cql_string += " like 'W0%'";
        break;
      case '2':
        cql_string += " like 'W1%'";
        break;
      case '3':
        cql_string += " like 'W2%'";
        break;
      case '4':
        cql_string += " like 'W3%'";
        break;
      case '5':
        cql_string += " like 'W4%'";
        break;
      case '6':
        cql_string += " like 'W5%'";
        break;
      case '7':
        cql_string += " like 'W6%'";
        break;
    }
  } else if (condition.attribute === 'irrig') {
    cql_string = condition.attribute;
    switch (singleValue) {
      case '1':
        cql_string += " like '1A%'";
        break;
      case '2':
        cql_string += " like '1B%' or irrig like '2A%' or irrig like '2B%'";
        break;
      case '3':
        cql_string +=
          " like '1C%' or irrig = '2' or irrig like '3A' or irrig like '3B' or irrig like '3C'";
        break;
      case '4':
        cql_string +=
          " like '1D%' or irrig = '4' or irrig like '2D%' or irrig like '3D%' or irrig like '4B%' or irrig like '4C%' or irrig like '4D%'";
        break;
      case '5':
        cql_string += " like 'U%'";
    }
  } else if (condition.attribute === 'text1' || condition.attribute === 'text2') {
    cql_string = condition.attribute;
    switch (singleValue) {
      case '1':
        cql_string += " in ('CS', 'GS', 'S', 'FS', 'GLS', 'LS', 'LFS')";
        break;
      case '2':
        cql_string += " in ('GSL', 'SL', 'FL', 'GL', 'VL')";
        break;
      case '3':
        cql_string += " in ('L', 'FCL', 'SCL', 'VCL')";
        break;
      case '4':
        cql_string += " in ('SIL', 'SICL', 'CL')";
        break;
      case '5':
        cql_string += " in ('SIC', 'C', 'HC')";
        break;
      case '6':
        cql_string += " in ('O')";
        break;
      case '7':
        cql_string += " in  ('U')";
        break;
    }
  } else if (condition.attribute === 'stone') {
    cql_string = 'stsym';
    switch (singleValue) {
      case '0':
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
        cql_string += `like 'S${parseInt(singleValue.charAt(0)) - 1}%'`;
        break;
    }
  } else if (condition.attribute === 'salsym') {
    cql_string = '';
    switch (singleValue) {
      case '1':
        cql_string += `salsym in (0)`;
        break;
      case '2':
        cql_string += `salsym like '1%' 
                    or salsym in ('2W') 
                    or salsym like '2W%'`;
        break;
      case '3':
        cql_string += `salsym in ('2','3','2M','2S','3M','3W','4W') 
                    or salsym like '2M%'
                    or salsym like '2S%'
                    or salsym like '3M%'
                    or salsym like '3W%'
                    or salsym like '4W%'`;
        break;
      case '4':
        cql_string += `
                    salsym in ('2V','3S','3V','4M','4S','5M','5W','6W','4','5')
                    or salsym like '2V%'
                    or salsym like '3S%'
                    or salsym like '3V%'
                    or salsym like '4M%'
                    or salsym like '4S%'
                    or salsym like '5M%'
                    or salsym like '5W%'
                    or salsym like '6W%'
                    `;
        break;
      case '5':
        cql_string += `
                    salsym in ('4V','5S','5V','6','6M','6S','6V')
                    or salsym like '4V%'
                    or salsym like '5S%'
                    or salsym like '5V%'
                    or salsym like '6M%'
                    or salsym like '6S%'
                    or salsym like '6V%'`;
        break;
    }
  } else {
    cql_string = condition.attribute;
    cql_string += " in ('";
    cql_string += singleValue;
    cql_string += "')";
  }

  return cql_string;
}

export function combineQueryConditions(conditions: QueryCondition[]): string {
  let combinedString = '';

  conditions.forEach((condition, i) => {
    const startParens = '('.repeat(condition.startParens);
    const endParens = ')'.repeat(condition.endParens);
    const cqlCondition = generateCqlFilterString(condition);

    if (i === 0) {
      combinedString += startParens + cqlCondition + endParens;
    } else {
      combinedString +=
        startParens + ' ' + condition.logicalOperator + ' ' + cqlCondition + endParens;
    }
  });

  return combinedString;
}
